import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { Card } from "../components/ui/primitives";

const PERSONA_COPY: Record<string, { title: string; body: string }> = {
  operator: { title: "Venue operator", body: "One location (Harbor Taproom)" },
  admin: { title: "Multi-location admin", body: "Two locations in the demo org" },
  staff: { title: "GoTab staff", body: "Two locations · sees generated SQL" },
};

/** gotab.io's own login. Without a login round-trip configured it can't send people back, so it opens in a popup. */
const GOTAB_LOGIN_URL = "https://gotab.io/cust/account/login";

const NO_ACCESS = "Your GoTab account doesn't have access to reports at any location. Ask a manager for the Manage Ordering permission, or sign in with a different account.";

/** Why GoTab sign-in (/api/auth/gotab) sent the browser back here. */
const SIGNIN_ERRORS: Record<string, string> = {
  not_operator: "You're signed in to gotab.io, but not with a GoTab manager account. Sign in with the phone number or email you use for the GoTab dashboard.",
  no_access: NO_ACCESS,
};

const Spinner = () => <Loader2 className="size-4 animate-spin text-subtle" />;

/**
 * Customer sign-in on GOTAB_AUTH_HOST. Anyone already signed in to gotab.io goes straight in. Everyone
 * else signs in on gotab.io (a round trip when configured, otherwise a popup) and this page continues
 * on its own as soon as that sign-in shows up.
 */
function GotabSignIn({ roundTrip }: { roundTrip: boolean }) {
  const signin = new URLSearchParams(window.location.search).get("signin");
  // Just signed out of dashboards but still signed in to gotab.io: don't sign them straight back in.
  const signedOut = signin === "signed_out";
  const popup = useRef<Window | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const status = useQuery({
    queryKey: ["gotab-status"],
    queryFn: api.gotabStatus,
    enabled: !signedOut,
    retry: false,
    // Keep checking until they're in; also while the gotab.io popup has focus.
    refetchInterval: (q) => (q.state.data?.access ? false : waiting ? 1500 : 3000),
    refetchIntervalInBackground: waiting,
  });
  const s = status.data;
  const autoRoundTrip = roundTrip && !signin && s && !s.signedIn;

  useEffect(() => {
    if (s?.access || autoRoundTrip) {
      popup.current?.close();
      setContinuing(true);
      window.location.assign("/api/auth/gotab");
    }
  }, [s?.access, autoRoundTrip]);

  const openLogin = () => {
    if (roundTrip) return window.location.assign("/api/auth/gotab");
    // Blocked popup: a new tab still works, it just can't be closed for them.
    popup.current = window.open(GOTAB_LOGIN_URL, "gotab-login", "popup,width=480,height=760") ?? window.open(GOTAB_LOGIN_URL, "_blank");
    popup.current?.focus();
    setWaiting(true);
  };

  if (continuing || (!signedOut && status.isLoading)) {
    return (
      <p className="mt-6 flex items-center gap-2 text-sm text-muted">
        <Spinner /> {continuing && !s?.access ? "Taking you to GoTab sign-in…" : continuing ? "Signing you in…" : "Checking your GoTab sign-in…"}
      </p>
    );
  }

  const message = signedOut
    ? "You're signed out of Custom Dashboards."
    : s?.signedIn && !s.access
      ? NO_ACCESS
      : status.isError
        ? "GoTab sign-in is unavailable right now. We'll keep trying."
        : signin && SIGNIN_ERRORS[signin];

  return (
    <>
      {message && <p className="mt-4 rounded-lg bg-surface-2 px-3 py-2 text-sm">{message}</p>}
      {waiting ? (
        <div className="mt-6 rounded-xl border border-border px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-medium"><Spinner /> Waiting for GoTab sign-in…</div>
          <p className="mt-1 text-xs text-muted">
            Finish signing in in the GoTab window. This page continues on its own.{" "}
            <button className="text-accent underline" onClick={openLogin}>Open it again</button>
          </p>
        </div>
      ) : (
        <button
          onClick={signedOut ? () => window.location.assign("/api/auth/gotab") : openLogin}
          className="group mt-6 flex w-full items-center gap-3 rounded-xl border border-border px-4 py-3 text-left transition-colors hover:border-border-strong hover:bg-surface-2"
        >
          <div className="flex-1">
            <div className="text-sm font-medium">{s?.signedIn && !s.access ? "Sign in with a different account" : "Continue with GoTab"}</div>
            <div className="text-xs text-muted">
              {signedOut ? "Sign back in with your gotab.io account" : "Sign in with the phone number or email you use for GoTab"}
            </div>
          </div>
          <ArrowRight className="size-4 text-subtle transition-transform group-hover:translate-x-0.5" />
        </button>
      )}
    </>
  );
}

export function Login() {
  const config = useQuery({ queryKey: ["auth-config"], queryFn: api.authConfig });
  if (config.isLoading) return null;
  return (
    <div className="flex h-full items-center justify-center p-6">
      <Card className="w-full max-w-md p-8">
        <h1 className="text-xl font-semibold tracking-tight">Custom Dashboards</h1>
        <p className="mt-2 text-sm text-muted">
          {config.data?.gotabSso
            ? "Sign in with your GoTab account. You'll see dashboards for the locations you manage."
            : "Open this app from your GoTab dashboard to sign in. You'll be taken straight to your dashboards with access to your locations."}
        </p>
        {config.data?.gotabSso && <GotabSignIn roundTrip={config.data.gotabLoginRoundTrip} />}
        {config.data?.staffSso && (
          <a
            href="/api/auth/staff"
            className="group mt-6 flex items-center gap-3 rounded-xl border border-border px-4 py-3 transition-colors hover:border-border-strong hover:bg-surface-2"
          >
            <div className="flex-1">
              <div className="text-sm font-medium">GoTab staff</div>
              <div className="text-xs text-muted">Sign in with your GoTab Google account, then choose locations</div>
            </div>
            <ArrowRight className="size-4 text-subtle transition-transform group-hover:translate-x-0.5" />
          </a>
        )}
        {config.data?.devLogin && (
          <div className="mt-6">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-subtle">Development sign-in</div>
            <div className="mt-2 space-y-2">
              {config.data.personas.map((p) => (
                <a
                  key={p}
                  href={`/api/auth/dev-login?persona=${p}`}
                  className="group flex items-center gap-3 rounded-xl border border-border px-4 py-3 transition-colors hover:border-border-strong hover:bg-surface-2"
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium">{PERSONA_COPY[p]?.title ?? p}</div>
                    <div className="text-xs text-muted">{PERSONA_COPY[p]?.body}</div>
                  </div>
                  <ArrowRight className="size-4 text-subtle transition-transform group-hover:translate-x-0.5" />
                </a>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
